import { useCallback, useMemo, useState } from "react";

import type { WebInvoiceLine } from "../lib/invoices";
import { gruppiereZeilen, istBlock } from "../lib/invoiceBlocks";
import {
  eintragAusBlock,
  eintragAusPosition,
  type AblageEintrag,
} from "../lib/invoiceClipboard";
import { positionAusZeile, type PackzettelPosition } from "../lib/packzettel";

/**
 * Mehrfachauswahl über Positionen und Blöcke.
 *
 * Gewählt wird über die **Zeilen-ID**, nicht über den Index: eine Auswahl,
 * die am Index hängt, zeigt nach jedem Verschieben oder Löschen auf die
 * falsche Zeile.
 *
 * Ein gewählter Block nimmt seine Positionen **immer** mit — er ist eine
 * Einheit. Deshalb zählt die Leiste zwei Zahlen getrennt: was ausdrücklich
 * gewählt wurde, und wie viele Zeilen daraus tatsächlich entstehen.
 */
export function useLineSelection(lines: WebInvoiceLine[]) {
  const [gewaehlteIds, setGewaehlteIds] = useState<Set<string>>(new Set());

  const umschalten = useCallback((id: string) => {
    setGewaehlteIds((vorher) => {
      const neu = new Set(vorher);
      if (neu.has(id)) neu.delete(id); else neu.add(id);
      return neu;
    });
  }, []);

  const aufheben = useCallback(() => setGewaehlteIds(new Set()), []);

  const gewaehlt = useCallback((id: string) => gewaehlteIds.has(id), [gewaehlteIds]);

  const zusammenfassung = useMemo(() => {
    // Zeilen, die zu einem gewählten Block gehören, zählen nicht noch einmal
    // als Einzelposition — sonst stünde in der Leiste „1 Block · 7 Positionen"
    // für sieben Zeilen, die längst im Block stecken.
    const gruppen = gruppiereZeilen(lines);
    const imGewaehltenBlock = new Set<string>();
    let bloecke = 0;
    let zeilenGesamt = 0;
    for (const gruppe of gruppen) {
      if (gruppe.block && gewaehlteIds.has(gruppe.block.id)) {
        bloecke += 1;
        zeilenGesamt += 1 + gruppe.zeilen.length;
        gruppe.zeilen.forEach(({ line }) => imGewaehltenBlock.add(line.id));
      }
    }
    const positionen = lines.filter((line) => (
      !istBlock(line) && gewaehlteIds.has(line.id) && !imGewaehltenBlock.has(line.id)
    )).length;
    return { bloecke, positionen, zeilen: zeilenGesamt + positionen };
  }, [lines, gewaehlteIds]);

  /**
   * Die Auswahl als Ablage-Einträge — in der Reihenfolge des Belegs, nicht in
   * der des Anklickens. Wer von oben nach unten auswählt, erwartet sie unten
   * genauso wieder.
   */
  const alsAblageEintraege = useCallback((quelle: string): AblageEintrag[] => {
    const eintraege: AblageEintrag[] = [];
    for (const gruppe of gruppiereZeilen(lines)) {
      // Ist der Block gewählt, wandert er als Einheit — seine Positionen
      // dürfen dann nicht zusätzlich einzeln in der Ablage landen.
      if (gruppe.block && gewaehlteIds.has(gruppe.block.id)) {
        eintraege.push(eintragAusBlock(gruppe.block, gruppe.zeilen.map((z) => z.line), quelle));
        continue;
      }
      for (const { line } of gruppe.zeilen) {
        if (gewaehlteIds.has(line.id)) eintraege.push(eintragAusPosition(line, quelle));
      }
    }
    return eintraege;
  }, [lines, gewaehlteIds]);

  /**
   * Die Auswahl als Packzettel-Positionen — **flach**.
   *
   * Ein Packzettel kennt keine Blöcke: der Subunternehmer bekommt eine Liste
   * von Leistungen. Ein gewählter Block wird deshalb in seine Positionen
   * aufgelöst, seine Überschrift fällt weg. Optionale Positionen bleiben
   * draußen — was der Kunde nicht beauftragt hat, kann niemand ausführen.
   */
  const alsPackzettelPositionen = useCallback((): PackzettelPosition[] => {
    const positionen: PackzettelPosition[] = [];
    for (const gruppe of gruppiereZeilen(lines)) {
      const blockGewaehlt = !!gruppe.block && gewaehlteIds.has(gruppe.block.id);
      for (const { line } of gruppe.zeilen) {
        if (line.line_kind !== "position") continue;
        if (blockGewaehlt || gewaehlteIds.has(line.id)) {
          positionen.push(positionAusZeile(line));
        }
      }
    }
    return positionen;
  }, [lines, gewaehlteIds]);

  return {
    gewaehlt, umschalten, aufheben, zusammenfassung,
    alsAblageEintraege, alsPackzettelPositionen,
    hatAuswahl: gewaehlteIds.size > 0,
    /** Die rohen IDs — für Umbauten der Liste wie „als Block zusammenfassen". */
    ids: gewaehlteIds,
  };
}
